import { Table, Tooltip } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../components/custom_components/Empty'
import { RoleName } from '../../types'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { PerTableActionButtons } from '../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerTag } from '../../components/custom_components/Tag'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import { removeHrRolesFromEmployee, removeDownloaderRoleFromEmployee } from '../../graphql/roles/hooks/useMutateRoles'
import { EmployeeWithRole, RoleOfUser } from '../../types'
import { GlobalAdminRemoveRoleModal } from './GlobalAdminRemoveRoleModal'
import { roleColor } from './utils'
import PerIcon from '../../components/custom_components/Icon'
import { showNotification } from '../../components/custom_components/Notification/notification'

interface GlobalAdminTableProps {
  employees: EmployeeWithRole[]
}

export const GlobalAdminTable: FC<GlobalAdminTableProps> = ({ employees }) => {
  const [t] = useTranslation()
  const [open, setOpen] = useState(false)
  const { user } = useAuth()
  const [selectedRoleOfUser, setSelectedRoleOfUser] = useState<string | undefined>(undefined)
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithRole | undefined>(
    employees && employees.length && employees[0] ? employees[0] : undefined,
  )
  const [modalError, setModalError] = useState<string | undefined>(undefined)

  const { tableActionButtonsRowWidth } = tableActionButtons()

  const removeRoleFromEmployee = async () => {
    if (selectedEmployee && selectedEmployee.hasDownloaderRole && selectedRoleOfUser === 'role-downloader') {
      const { errors } = await removeDownloaderRoleFromEmployee(selectedEmployee.id)
      if (!!errors && !!errors.length) {
        setModalError(t('global.notifications.error.description'))
      } else {
        showNotification({
          type: 'success',
          message: t('globalAdminView.table.notifications.delete.downloader.success.title'),
          description: t('globalAdminView.table.notifications.delete.downloader.success.description', {
            employeeFirstName: selectedEmployee.firstName,
          }),
        })
      }
    } else {
      const _rolesOfEmployee = selectedEmployee && selectedEmployee.hrRoleOfUser ? selectedEmployee.hrRoleOfUser : []
      const selectedRole = _rolesOfEmployee.find((roleOfUser) => roleOfUser.id === selectedRoleOfUser)
      const toBeDeletedHrRoleOfUser = _rolesOfEmployee.find(
        (roleOfUser) =>
          selectedRole &&
          roleOfUser.fromDate === selectedRole.fromDate &&
          roleOfUser.id !== selectedRoleOfUser &&
          roleOfUser.role.name === RoleName.Hr,
      )
      if (selectedEmployee && selectedRole && toBeDeletedHrRoleOfUser) {
        const roleOfUserIds = [selectedRole.id, toBeDeletedHrRoleOfUser.id]
        const restrictedRole = _rolesOfEmployee
          .filter((employeeRole: RoleOfUser) => employeeRole.role.name.includes('HR_'))
          .filter((employeeRole: RoleOfUser) => {
            const roleParts = employeeRole.role.name.split('_')
            return roleParts[1] === 'RESTRICTED'
          })
          .find((roleOfUser) => roleOfUser.role.name.split('_')[2] === selectedRole.role.name.split('_')[2])
        if (restrictedRole) {
          roleOfUserIds.push(restrictedRole.id)
        }
        const { errors } = await removeHrRolesFromEmployee(selectedEmployee.id, roleOfUserIds, selectedRole.role.name)
        if (!!errors && !!errors.length) {
          setModalError(t('global.notifications.error.description'))
        } else {
          showNotification({
            type: 'success',
            message: t('globalAdminView.table.notifications.delete.hr.success.title'),
            description: t('globalAdminView.table.notifications.delete.hr.success.description', {
              employeeFirstName: selectedEmployee.firstName,
            }),
          })
        }
      }
    }
    onCancel()
  }

  const onCancel = () => {
    setOpen(false)
    setSelectedRoleOfUser(undefined)
    setSelectedEmployee(employees[0])
    setModalError(undefined)
  }

  const columns = [
    {
      key: 'globalAdmin-employee',
      title: <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('globalAdminView.table.employee.heading')}</Tooltip>,
      defaultSortOrder: 'ascend' as SortOrder,
      className: 'textAlign-left minWidth-column-l',
      sorter: (employeeA, employeeB) => {
        return employeeA.firstName && employeeB.firstName && employeeA.firstName.localeCompare(employeeB.firstName)
      },
      render: (employee: EmployeeWithRole) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={employee.id}
          firstName={employee.firstName}
          lastName={employee.lastName}
          infoText={employee.companyName + ' | ' + employee.buName}
        />
      ),
    },
    {
      key: 'globalAdmin-downloader',
      title: t('globalAdminView.table.downloader.heading'),
      defaultSortOrder: 'ascend' as SortOrder,
      className: 'textAlign-center minWidth-column-m',
      sorter: (employeeA, employeeB) => {
        return employeeA.hasDownloaderRole - employeeB.hasDownloaderRole
      },
      render: (employee: EmployeeWithRole) => {
        return <>{!!employee.hasDownloaderRole && <PerIcon name="check_stroke" color="success" variant="decoration" size={16} />}</>
      },
    },
    {
      key: 'globalAdmin-assignedTo',
      title: t('globalAdminView.table.assignedTo.heading'),
      className: 'textAlign-left minWidth-column-l',
      sorter: (employeeA, employeeB) => {
        return employeeA.roleOfUser[0].role.name.localeCompare(employeeB.roleOfUser[0].role.name)
      },
      render: (employee: EmployeeWithRole) => {
        const employeeHRRoles = employee.hrRoleOfUser
          .filter((employeeRole: RoleOfUser) => employeeRole.role.name.includes('HR_'))
          .filter((employeeRole: RoleOfUser) => {
            const roleParts = employeeRole.role.name.split('_')
            return roleParts[1] === 'ADMIN' || roleParts[1] === 'READONLY'
          })
        const employeeRestrictedRoles = employee.hrRoleOfUser
          .filter((employeeRole: RoleOfUser) => employeeRole.role.name.includes('HR_'))
          .filter((employeeRole: RoleOfUser) => {
            const roleParts = employeeRole.role.name.split('_')
            return roleParts[1] === 'RESTRICTED'
          })
        const employeeRestrictedCountry = employeeRestrictedRoles.map((role: RoleOfUser) => {
          const country = role.role.name.split('_')[2]
          return country
        })

        return (
          <>
            {employeeHRRoles
              .sort((a: RoleOfUser, b: RoleOfUser) => {
                const countryA = t(`global.country.${a.role.name.split('_')[2]}`)
                const countryB = t(`global.country.${b.role.name.split('_')[2]}`)
                return countryA.localeCompare(countryB)
              })
              .map((employeeRole: RoleOfUser) => {
                const roleParts = employeeRole.role.name.split('_')

                return (
                  <PerFlexRow mb={'xxxs'} key={employeeRole.id} align="middle">
                    <PerText mr={'xs'}>{t(`global.country.${roleParts[2]}`)}</PerText>
                    <PerTag color={roleColor(employeeRole.role.name)} key={`${employee.id}_${employeeRole.id}`}>
                      {roleParts[1]}
                    </PerTag>
                    {employeeRestrictedCountry.length > 0 && employeeRestrictedCountry.includes(roleParts[2]) && (
                      <PerText fontSize="xs" color="secondary" fontWeight="normal" ml="xxxs">
                        {t('globalAdminView.table.assignedTo.hrExcluded')}
                      </PerText>
                    )}
                  </PerFlexRow>
                )
              })}
          </>
        )
      },
    },
    {
      key: 'reviewer-actions',
      title: t('globalAdminView.table.actions.heading'),
      width: tableActionButtonsRowWidth(1),
      className: 'textAlign-left',
      render: (employee) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'delete_fill',
              color: 'error',
              tooltip: t('globalAdminView.table.actions.deleteBtnTooltip', {
                employee: employee.firstName + ' ' + employee.lastName,
              }),
              onClick: () => {
                setSelectedEmployee(employee)
                setOpen(true)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <GlobalAdminRemoveRoleModal
        open={open}
        onCancel={onCancel}
        deleteRole={removeRoleFromEmployee}
        employee={selectedEmployee}
        selectedRoleOfUser={selectedRoleOfUser}
        setSelectedRoleOfUser={setSelectedRoleOfUser}
        error={modalError}
      />

      <Table
        className="light viewWithTabsAndInfoBox scroll-compensation"
        dataSource={employees.map((employee) => ({ ...employee, key: employee.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: <PerEmpty icon="employeesNo_stroke" heading={t('globalAdminView.table.empty.heading')} />,
        }}
      />
    </>
  )
}
